/**
 * Pagina della watchlist, disegnata come la vista principale di un terminale:
 * la tabella dei titoli È la pagina (tutta la larghezza disponibile), il
 * grafico è un pannello fisso a lato che segue la riga selezionata, l'aggiunta
 * di uno strumento è un dialog secondario aperto dall'header — non un form
 * sempre visibile che ruba la parte alta dello schermo.
 */

import { Fragment, useCallback, useState, useSyncExternalStore } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMarkets, useResolveInstrumentMarket, useSetInstrumentMarket } from '@/features/markets/api';
import { STATUS_LABELS, STATUS_TONE } from '@/features/markets/market-status-format';
import '@/features/markets/markets.css';
import { ApiError } from '@/lib/api-client';
import { formatCurrency, formatDate, formatDateTime, formatPercent } from '@/lib/format';
import { Alert, Badge, Button, Card, EmptyState, Field, IconButton, Modal, Select, Spinner, TableWrap } from '@/ui';
import { ValueTrendChart } from '@/ui/ValueTrendChart';
import { useWishlist } from '@/features/wishlist/api';
import { WishlistItemModal } from '@/features/wishlist/WishlistItemModal';
import { AiAnalysisSection } from './AiAnalysisSection';
import type { AnalysisRating } from './ai-analysis-api';
import { RATING_TONE } from './analysis-rating';
import {
  useAddWatchlistItem,
  useKeepWatchlistItem,
  useRemoveWatchlistItem,
  useWatchlist,
  useWatchlistItemHistory,
  useWatchlistStats,
  WATCHLIST_SOURCE_LABELS,
  type WatchlistItem,
} from './api';
import { MappingCandidatesModal } from './MappingCandidatesModal';
import {
  applyWatchlistView,
  availableMics,
  DEFAULT_VIEW,
  toggleSort,
  type SortKey,
  type WatchlistView,
} from './watchlist-filter';
import './watchlist.css';
import { InstrumentLogo } from '@/features/instruments/InstrumentLogo';

/**
 * Il valore convertito in valuta base è il dato primario (SPEC-004 §5, TODO
 * "uniforma i prezzi"): l'originale resta visibile ma in secondo piano, mai
 * sostituito — un titolo GBP deve restare confrontabile con uno EUR a colpo
 * d'occhio, senza nascondere il prezzo che il mercato mostra davvero.
 */
function PriceCell({
  amount,
  currency,
  amountBase,
  baseCurrency,
}: {
  amount: string | null;
  currency: string | null;
  amountBase: string | null;
  baseCurrency: string;
}) {
  if (!amount) return <>—</>;
  if (!amountBase) return <>{formatCurrency(amount, currency ?? undefined)}</>;

  return (
    <span className="watchlist__price">
      <span className="watchlist__price-base">{formatCurrency(amountBase, baseCurrency)}</span>
      <span className="watchlist__price-original">{formatCurrency(amount, currency ?? undefined)}</span>
    </span>
  );
}

function DeltaBadge({ deltaPct }: { deltaPct: string | null }) {
  if (deltaPct === null) return <span>—</span>;
  const numeric = Number(deltaPct);
  const tone = numeric > 0 ? 'positive' : numeric < 0 ? 'negative' : 'neutral';
  return <Badge tone={tone}>{formatPercent(deltaPct)}</Badge>;
}

/** Perché manca il prezzo: un mapping ISIN ambiguo/non trovato non è un bug, ma va spiegato. */
function MappingHint({ item }: { item: WatchlistItem }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const { mappingStatus } = item.instrument;

  if (mappingStatus === 'ambiguous') {
    return (
      <>
        <span className="watchlist__mapping-hint">
          Mapping ambiguo —{' '}
          <button type="button" className="watchlist__mapping-resolve" onClick={() => setPickerOpen(true)}>
            scegli il ticker
          </button>
        </span>
        {pickerOpen && (
          <MappingCandidatesModal
            instrumentId={item.instrument.id}
            instrumentName={item.instrument.name}
            onClose={() => setPickerOpen(false)}
          />
        )}
      </>
    );
  }
  if (mappingStatus === 'not_found') {
    return (
      <span className="watchlist__mapping-hint" title="Nessun mercato trovato per questo ISIN al momento: si ritenta periodicamente">
        Nessun mercato trovato — ritentato periodicamente
      </span>
    );
  }
  if (!mappingStatus) {
    return <span className="watchlist__mapping-hint">Mapping in corso di risoluzione…</span>;
  }
  return null;
}

/** Tipi con un vero orario di borsa: stesso filtro di `MARKET_RESOLVABLE_INSTRUMENT_TYPES` nel backend (SPEC-010 §4). */
const MARKET_RESOLVABLE_TYPES = new Set(['stock', 'etf', 'fund']);

/**
 * Sigla + stato aperto/chiuso del mercato di quotazione (SPEC-010 §8.3); se
 * non ancora risolto, offre di risolverlo — anche per strumenti senza ISIN
 * (es. aggiunti per simbolo, F-016): il mercato si deriva dal suffisso del
 * ticker Yahoo già noto (`AZN.L` → Londra), non serve un ISIN.
 */
function MarketBadge({ item }: { item: WatchlistItem }) {
  const { marketMic, marketResolutionStatus, instrumentType } = item.instrument;
  const marketsQuery = useMarkets();
  const resolve = useResolveInstrumentMarket(item.instrument.id);
  const setMarket = useSetInstrumentMarket(item.instrument.id);
  const [selectedMic, setSelectedMic] = useState('');

  if (marketMic) {
    const market = marketsQuery.data?.items?.find((candidate) => candidate.mic === marketMic);
    return (
      <span className="markets__badge">
        <Badge tone={market ? STATUS_TONE[market.status] : 'neutral'}>
          {marketMic}
          {market ? ` · ${STATUS_LABELS[market.status]}` : ''}
        </Badge>
      </span>
    );
  }

  if (!MARKET_RESOLVABLE_TYPES.has(instrumentType)) return <span className="text-muted">—</span>;

  const resolveError = resolve.error instanceof ApiError ? resolve.error : null;
  const setMarketError = setMarket.error instanceof ApiError ? setMarket.error : null;

  // Il tentativo e' andato a buon fine ma non ha prodotto un mercato: il
  // backend dice perche' (SPEC-010, caso MAT1.F). Senza questo ramo il pulsante
  // restava premibile all'infinito senza cambiare niente, e sembrava rotto.
  const attemptReason = resolve.data?.unresolvedReason ?? null;

  if (marketResolutionStatus === 'ambiguous' || attemptReason) {
    return (
      <div className="markets__resolve-picker">
        <Select
          label={attemptReason ? 'Mercato da scegliere' : 'Mercato ambiguo'}
          value={selectedMic}
          onChange={(event) => setSelectedMic(event.target.value)}
          placeholder="Scegli il mercato…"
          options={(marketsQuery.data?.items ?? []).map((market) => ({ value: market.mic, label: `${market.mic} — ${market.name}` }))}
          error={setMarketError?.message}
          hint={attemptReason ?? undefined}
        />
        <Button
          variant="ghost"
          disabled={!selectedMic}
          loading={setMarket.isPending}
          onClick={() => setMarket.mutate(selectedMic)}
        >
          Conferma
        </Button>
      </div>
    );
  }

  return (
    <div className="markets__resolve-picker">
      <Button variant="ghost" loading={resolve.isPending} onClick={() => resolve.mutate()}>
        Risolvi mercato
      </Button>
      {resolveError && <span className="watchlist__mapping-hint">{resolveError.message}</span>}
    </div>
  );
}

function LatestAnalysisCell({ item }: { item: WatchlistItem }) {
  if (!item.latestAnalysis) return <span>—</span>;
  const { id, rating, completedAt } = item.latestAnalysis;
  return (
    <Link to={`/watchlists/analyses/${item.instrument.id}/${id}`} className="watchlist__analysis-cell">
      {rating ? <Badge tone={RATING_TONE[rating as AnalysisRating]}>{rating}</Badge> : <span>—</span>}
      <span className="watchlist__analysis-date">{formatDate(completedAt)}</span>
    </Link>
  );
}

function ItemHistory({ item }: { item: WatchlistItem }) {
  const historyQuery = useWatchlistItemHistory(item.id, true);

  if (historyQuery.isPending) return <Spinner label="Caricamento dello storico…" />;
  if (historyQuery.isError) {
    return (
      <Alert variant="error" title="Impossibile caricare lo storico">
        {historyQuery.error instanceof ApiError ? historyQuery.error.message : 'Errore imprevisto.'}
      </Alert>
    );
  }

  const points = historyQuery.data.items.map((point) => ({ date: point.capturedAt, value: point.price }));
  const currency = item.lastPriceCurrency ?? item.addedPriceCurrency ?? item.instrument.currency;

  if (points.length === 0) {
    return (
      <EmptyState
        icon="📈"
        title="Nessuna quotazione ancora registrata"
        description="Il job orario scrive un punto nuovo a ogni giro; il grafico comparirà dopo la prima esecuzione utile."
      />
    );
  }

  return (
    <ValueTrendChart
      points={points}
      currency={currency}
      label={`Prezzo di ${item.instrument.name}`}
      showTime
    />
  );
}

/* Soglia del layout a due pannelli: sotto, il grafico fisso non esiste e lo
   storico resta quello espandibile per riga — stessa informazione, meno
   ingombro. La soglia è più alta degli 880px del layout perché tabella densa
   e pannello affiancati richiedono larghezza vera, non solo "non mobile". */
const WIDE_VIEWPORT_QUERY = '(min-width: 1100px)';

/* Sotto questa soglia la tabella cede il posto a una lista compatta: nove
   colonne su uno schermo da telefono significano scorrimento orizzontale e
   due dati visibili per volta, cioè nessuna vista d'insieme. */
const MOBILE_VIEWPORT_QUERY = '(max-width: 880px)';

/**
 * Le due varianti sono componenti diversi, non CSS che nasconde: `display:none`
 * lascerebbe comunque montare (e far fetchare) il ramo invisibile. Il guard su
 * `matchMedia` copre jsdom, che non lo implementa: lì vale sempre `false`,
 * quindi i test vedono la vista desktop se non lo simulano esplicitamente.
 */
function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof window.matchMedia !== 'function') return () => {};
      const mql = window.matchMedia(query);
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    },
    [query],
  );
  return useSyncExternalStore(
    subscribe,
    () => typeof window.matchMedia === 'function' && window.matchMedia(query).matches,
  );
}

/**
 * Riga della lista mobile: una sola riga battuta a due colonne — identità a
 * sinistra, quotazione a destra — così da tenerne molte sullo schermo. Tutto
 * il resto (mercato, analisi, date, azioni, grafico) sta nel pannello che si
 * apre al tocco: su un telefono il dettaglio si chiede, non si subisce.
 */
/**
 * Una voce messa da un automatismo lo dice, insieme alla data in cui uscirà da
 * sola se nessuno la ripropone (F-181): una riga non deve sparire dalla
 * watchlist senza preavviso. «Tieni» la rende dell'utente.
 *
 * `onKeep` manca dove il testo sta dentro un altro pulsante (la riga mobile):
 * lì il «Tieni» vive fra le azioni del dettaglio.
 */
function AutoSourceHint({
  item,
  onKeep,
  keeping = false,
}: {
  item: WatchlistItem;
  onKeep?: () => void;
  keeping?: boolean;
}) {
  if (item.source === 'manual') return null;
  return (
    <span className="watchlist__auto">
      automatica, {WATCHLIST_SOURCE_LABELS[item.source]}
      {item.autoRemovalAt && <> · esce il {formatDate(item.autoRemovalAt)}</>}
      {onKeep && (
        <button
          type="button"
          className="watchlist__auto-keep"
          disabled={keeping}
          title="Tienila in watchlist: non uscirà più da sola"
          onClick={(event) => {
            event.stopPropagation();
            onKeep();
          }}
        >
          Tieni
        </button>
      )}
    </span>
  );
}

function MobileItemRow({
  item,
  expanded,
  onToggle,
  onRemove,
  onWishlist,
  onKeep,
  removing,
  keeping,
}: {
  item: WatchlistItem;
  expanded: boolean;
  onToggle: () => void;
  onRemove: () => void;
  onWishlist: () => void;
  onKeep: () => void;
  removing: boolean;
  keeping: boolean;
}) {
  const [analysisOpen, setAnalysisOpen] = useState(false);

  return (
    <li className={expanded ? 'wl-item wl-item--expanded' : 'wl-item'}>
      {/* Solo span dentro il bottone: qualunque controllo annidato (il picker
          del mapping, i link) sta nel pannello di dettaglio, mai qui. */}
      <button type="button" className="wl-item__row" aria-expanded={expanded} onClick={onToggle}>
        <span className="wl-item__identity">
          <span className="wl-item__name">
            <InstrumentLogo instrumentId={item.instrument.id} name={item.instrument.name} />
            {item.instrument.name}
          </span>
          <span className="wl-item__meta">
            {item.instrument.symbol ?? item.instrument.isin ?? '—'}
            {item.instrument.marketMic && <> · {item.instrument.marketMic}</>}
            {item.inPortfolio && <> · in portafoglio</>}
          </span>
          <AutoSourceHint item={item} />
        </span>
        <span className="wl-item__quote">
          <span className="wl-item__price">
            <PriceCell
              amount={item.lastPrice}
              currency={item.lastPriceCurrency}
              amountBase={item.lastPriceBase}
              baseCurrency={item.baseCurrency}
            />
          </span>
          <DeltaBadge deltaPct={item.deltaPct} />
        </span>
      </button>

      {expanded && (
        <div className="wl-item__detail">
          {!item.lastPrice && <MappingHint item={item} />}
          <MarketBadge item={item} />

          <dl className="watchlist__facts">
            <dt>Prezzo iniziale</dt>
            <dd className="numeric">
              <PriceCell
                amount={item.addedPrice}
                currency={item.addedPriceCurrency}
                amountBase={item.addedPriceBase}
                baseCurrency={item.baseCurrency}
              />
            </dd>
            <dt>Aggiunto il</dt>
            <dd className="numeric">{formatDate(item.addedAt)}</dd>
            {item.lastPriceAt && (
              <>
                <dt>Ultima quotazione</dt>
                <dd className="numeric">{formatDateTime(item.lastPriceAt)}</dd>
              </>
            )}
            {item.latestAnalysis && (
              <>
                <dt>Analisi AI</dt>
                <dd>
                  <LatestAnalysisCell item={item} />
                </dd>
              </>
            )}
          </dl>
          {item.notes && <p className="watchlist__panel-notes">{item.notes}</p>}

          {/* Il grafico è già qui: su mobile un ulteriore livello "Storico" da
              aprire sarebbe un tocco in più per il dato più richiesto. */}
          <ItemHistory item={item} />

          <div className="wl-item__actions">
            {/* Il nome sta dentro il bottone di espansione (niente link annidati):
                la scheda del titolo si apre da qui (SPEC-016). */}
            <Link className="btn btn--secondary" to={`/instruments/${item.instrument.id}`}>
              Scheda titolo
            </Link>
            <Button variant="secondary" onClick={() => setAnalysisOpen((open) => !open)}>
              {analysisOpen ? 'Nascondi analisi AI' : 'Analisi AI'}
            </Button>
            <Button variant="ghost" onClick={onWishlist}>
              Wishlist
            </Button>
            {item.source !== 'manual' && (
              <Button variant="ghost" loading={keeping} onClick={onKeep}>
                Tieni
              </Button>
            )}
            <Button variant="ghost" loading={removing} onClick={onRemove}>
              Rimuovi
            </Button>
          </div>
          {analysisOpen && <AiAnalysisSection item={item} />}
        </div>
      )}
    </li>
  );
}

/**
 * Pannello fisso a lato della tabella: prezzo, variazione, fatti essenziali e
 * grafico del titolo selezionato cliccando la riga.
 */
function ChartPanel({ item }: { item: WatchlistItem }) {
  return (
    <Card
      title={item.instrument.name}
      subtitle={item.instrument.isin ?? item.instrument.symbol ?? undefined}
    >
      <div className="watchlist__panel-price">
        <PriceCell
          amount={item.lastPrice}
          currency={item.lastPriceCurrency}
          amountBase={item.lastPriceBase}
          baseCurrency={item.baseCurrency}
        />
        <DeltaBadge deltaPct={item.deltaPct} />
      </div>

      <dl className="watchlist__facts">
        <dt>Prezzo iniziale</dt>
        <dd className="numeric">
          <PriceCell
            amount={item.addedPrice}
            currency={item.addedPriceCurrency}
            amountBase={item.addedPriceBase}
            baseCurrency={item.baseCurrency}
          />
        </dd>
        {item.targetPrice && (
          <>
            <dt>Prezzo obiettivo</dt>
            <dd className="numeric">{formatCurrency(item.targetPrice, item.instrument.currency)}</dd>
          </>
        )}
        <dt>Aggiunto il</dt>
        <dd className="numeric">{formatDate(item.addedAt)}</dd>
        {item.lastPriceAt && (
          <>
            <dt>Ultima quotazione</dt>
            <dd className="numeric">{formatDateTime(item.lastPriceAt)}</dd>
          </>
        )}
      </dl>
      {item.notes && <p className="watchlist__panel-notes">{item.notes}</p>}

      <ItemHistory item={item} />
    </Card>
  );
}

type InputMode = 'isin' | 'symbol';

/**
 * Aggiunta di uno strumento in un dialog: è un'azione occasionale, non merita
 * la parte alta della pagina (la tabella è l'elemento principale). Lo stato
 * del form vive qui dentro: chiudere il dialog lo azzera.
 */
function AddInstrumentModal({ onClose }: { onClose: () => void }) {
  const addItem = useAddWatchlistItem();
  const [mode, setMode] = useState<InputMode>('isin');
  const [identifier, setIdentifier] = useState('');
  const [notes, setNotes] = useState('');

  const handleAdd = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = identifier.trim().toUpperCase();
    const input = mode === 'isin'
      ? { isin: trimmed, notes: notes.trim() || undefined }
      : { symbol: trimmed, notes: notes.trim() || undefined };
    addItem.mutate(input, { onSuccess: onClose });
  };

  const addError = addItem.error instanceof ApiError ? addItem.error : null;
  const fieldErrorPath = mode === 'isin' ? 'isin' : 'symbol';

  return (
    <Modal title="Aggiungi uno strumento" onClose={onClose}>
      <p className="watchlist__form-hint">
        Per ISIN lo strumento deve essere già a catalogo; per simbolo ne viene creato uno nuovo se necessario.
      </p>
      <form className="watchlist__form" onSubmit={handleAdd}>
        <div className="watchlist__mode-switch" role="radiogroup" aria-label="Modalità di ricerca">
          <label className={mode === 'isin' ? 'active' : ''}>
            <input
              type="radio"
              name="mode"
              value="isin"
              checked={mode === 'isin'}
              onChange={() => { setMode('isin'); setIdentifier(''); }}
            />
            ISIN
          </label>
          <label className={mode === 'symbol' ? 'active' : ''}>
            <input
              type="radio"
              name="mode"
              value="symbol"
              checked={mode === 'symbol'}
              onChange={() => { setMode('symbol'); setIdentifier(''); }}
            />
            Simbolo
          </label>
        </div>
        <Field
          label={mode === 'isin' ? 'ISIN' : 'Simbolo'}
          value={identifier}
          onChange={(event) => setIdentifier(event.target.value)}
          placeholder={mode === 'isin' ? 'IE00BK5BQT80' : 'AAPL'}
          required
          maxLength={20}
          error={
            addError?.isValidation
              ? (addError.details.find((detail) => detail.path.includes(fieldErrorPath))?.message ?? undefined)
              : undefined
          }
        />
        <Field
          label="Note (opzionale)"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="Perché lo tengo d'occhio"
        />
        <Button type="submit" loading={addItem.isPending}>
          Aggiungi
        </Button>
      </form>
      {addError && !addError.isValidation && (
        <Alert variant="error" title="Impossibile aggiungere lo strumento">
          {addError.message}
        </Alert>
      )}
    </Modal>
  );
}

/**
 * Intestazione di colonna ordinabile. È un `<button>` dentro il `<th>`, non un
 * `onClick` sul `<th>`: solo così l'ordinamento è raggiungibile da tastiera, e
 * `aria-sort` dice a uno screen reader su quale colonna e in che verso.
 */
function SortableHeader({
  label,
  sortKey,
  view,
  onSort,
  className,
}: {
  label: string;
  sortKey: SortKey;
  view: WatchlistView;
  onSort: (key: SortKey) => void;
  className?: string;
}) {
  const active = view.sortKey === sortKey;
  const ariaSort = active ? (view.sortDirection === 'asc' ? 'ascending' : 'descending') : 'none';
  return (
    <th className={className} aria-sort={ariaSort}>
      <button
        type="button"
        className={active ? 'watchlist__sort watchlist__sort--active' : 'watchlist__sort'}
        onClick={() => onSort(sortKey)}
      >
        {label}
        <span aria-hidden="true" className="watchlist__sort-arrow">
          {active ? (view.sortDirection === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}

/** Ricerca, filtri e conteggio del risultato: una riga sopra la tabella, non un pannello da aprire. */
function WatchlistFilters({
  view,
  onChange,
  mics,
  shown,
  total,
}: {
  view: WatchlistView;
  onChange: (next: WatchlistView) => void;
  mics: string[];
  shown: number;
  total: number;
}) {
  const filtered = shown !== total;
  return (
    <div className="watchlist__filters">
      <input
        type="search"
        className="watchlist__search"
        value={view.query}
        onChange={(event) => onChange({ ...view, query: event.target.value })}
        placeholder="Cerca per nome, ISIN, simbolo, note…"
        aria-label="Cerca nella watchlist"
      />
      <Select
        label="Portafoglio"
        value={view.portfolio}
        onChange={(event) => onChange({ ...view, portfolio: event.target.value as WatchlistView['portfolio'] })}
        options={[
          { value: 'all', label: 'Tutti' },
          { value: 'in', label: 'In portafoglio' },
          { value: 'out', label: 'Solo osservati' },
        ]}
      />
      <Select
        label="Mercato"
        value={view.mic}
        onChange={(event) => onChange({ ...view, mic: event.target.value })}
        placeholder="Tutti"
        options={[
          ...mics.map((mic) => ({ value: mic, label: mic })),
          { value: 'none', label: 'Non risolto' },
        ]}
      />
      <span className="watchlist__filters-count">
        {filtered ? `${shown} di ${total} titoli` : `${total} titoli`}
      </span>
      {filtered && (
        <Button variant="ghost" onClick={() => onChange(DEFAULT_VIEW)}>
          Azzera filtri
        </Button>
      )}
    </div>
  );
}

export function WatchlistPage() {
  const watchlistQuery = useWatchlist();
  const statsQuery = useWatchlistStats();
  const removeItem = useRemoveWatchlistItem();
  const keepItem = useKeepWatchlistItem();
  // Serve solo la finestra di default da mostrare nel modal: duplicarla in una
  // costante del frontend la farebbe divergere da WISHLIST_DEFAULT_EXPIRY_DAYS.
  const wishlistQuery = useWishlist();

  const [addOpen, setAddOpen] = useState(false);
  // Il titolo da candidare all'acquisto (SPEC-012.2 §4.1): il modal si apre da
  // qui perché è qui che il titolo esiste già, ed è la precondizione della riga.
  // `symbol` serve solo a precompilare la ricerca del ticker broker nel modal:
  // il nome esteso trova meno spesso una corrispondenza a catalogo.
  const [wishlistFor, setWishlistFor] = useState<{ id: string; name: string; symbol: string | null } | null>(null);
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
  const [expandedAnalysisItemId, setExpandedAnalysisItemId] = useState<string | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const [view, setView] = useState<WatchlistView>(DEFAULT_VIEW);

  const isWide = useMediaQuery(WIDE_VIEWPORT_QUERY);
  const isMobile = useMediaQuery(MOBILE_VIEWPORT_QUERY);
  const allItems = watchlistQuery.data?.items ?? [];
  const items = applyWatchlistView(allItems, view);
  // Primo titolo selezionato di default: il pannello non deve mai stare vuoto.
  // Si sceglie fra i titoli *visibili*, altrimenti un filtro lascerebbe a lato
  // il grafico di una riga che non compare più in tabella.
  const selectedItem = items.find((candidate) => candidate.id === selectedItemId) ?? items[0] ?? null;
  const handleSort = (key: SortKey) => setView((current) => toggleSort(current, key));

  return (
    <div className="watchlist">
      <header className="watchlist__header">
        <div>
          <h1 className="watchlist__title">Watchlist</h1>
          <p className="watchlist__subtitle">
            {statsQuery.data?.limits
              ? // F-195: i due limiti sono separati, quindi si mostrano separati —
                // «48 su 70» direbbe che c'è posto anche quando la borsa è piena.
                `${statsQuery.data.limits.stock.count}/${statsQuery.data.limits.stock.max} titoli di borsa · ${statsQuery.data.limits.crypto.count}/${statsQuery.data.limits.crypto.max} criptovalute (${statsQuery.data.distinctInstrumentsGlobal} strumenti distinti nel sistema)`
              : statsQuery.data
                ? // Un backend non ancora aggiornato (durante un deploy) non manda i limiti.
                  `${statsQuery.data.itemsCount} strumenti monitorati (${statsQuery.data.distinctInstrumentsGlobal} distinti nel sistema)`
                : 'Titoli monitorati, anche non a portafoglio'}
          </p>
        </div>
        <div className="watchlist__header-actions">
          <Button onClick={() => setAddOpen(true)}>＋ Aggiungi strumento</Button>
          <Link to="/markets" className="btn btn--secondary">
            🏛️ Mercati
          </Link>
        </div>
      </header>

      {addOpen && <AddInstrumentModal onClose={() => setAddOpen(false)} />}

      {wishlistFor && (
        <WishlistItemModal
          instrument={wishlistFor}
          defaultExpiryDays={wishlistQuery.data?.defaultExpiryDays ?? 7}
          onClose={() => setWishlistFor(null)}
        />
      )}

      <div className="watchlist__split">
        <Card title="Titoli monitorati" flush>
          {watchlistQuery.isPending ? (
            <Spinner label="Caricamento della watchlist…" />
          ) : watchlistQuery.isError ? (
            <Alert variant="error" title="Impossibile caricare la watchlist">
              {watchlistQuery.error instanceof ApiError ? watchlistQuery.error.message : 'Errore imprevisto.'}
            </Alert>
          ) : allItems.length === 0 ? (
            <EmptyState
              icon="⭐"
              title="Nessuno strumento in watchlist"
              description="Aggiungi un ISIN o un simbolo per iniziare a monitorarne il prezzo, anche senza averlo in portafoglio."
              action={<Button onClick={() => setAddOpen(true)}>＋ Aggiungi strumento</Button>}
            />
          ) : (
            <>
              <WatchlistFilters
                view={view}
                onChange={setView}
                mics={availableMics(allItems)}
                shown={items.length}
                total={allItems.length}
              />
              {items.length === 0 ? (
                // Nessun pulsante qui: "Azzera filtri" è già nella barra due
                // righe sopra, e due controlli identici affiancati confondono.
                <EmptyState
                  icon="🔍"
                  title="Nessun titolo corrisponde ai filtri"
                  description="Allarga la ricerca o azzera i filtri per rivedere tutta la watchlist."
                />
              ) : isMobile ? (
            <ul className="wl-list">
              {items.map((item) => (
                <MobileItemRow
                  key={item.id}
                  item={item}
                  expanded={expandedItemId === item.id}
                  onToggle={() => setExpandedItemId(expandedItemId === item.id ? null : item.id)}
                  onRemove={() => removeItem.mutate(item.id)}
                  onWishlist={() =>
                    setWishlistFor({
                      id: item.instrument.id,
                      name: item.instrument.name,
                      symbol: item.instrument.symbol,
                    })
                  }
                  onKeep={() => keepItem.mutate(item.id)}
                  removing={removeItem.isPending && removeItem.variables === item.id}
                  keeping={keepItem.isPending && keepItem.variables === item.id}
                />
              ))}
            </ul>
          ) : (
            <TableWrap tall>
              <table className="table table--compact table--sticky-head">
                <thead>
                  <tr>
                    <SortableHeader label="Strumento" sortKey="name" view={view} onSort={handleSort} />
                    <SortableHeader label="Ultimo prezzo" sortKey="lastPrice" view={view} onSort={handleSort} className="numeric" />
                    <SortableHeader label="Variazione" sortKey="deltaPct" view={view} onSort={handleSort} className="numeric" />
                    <SortableHeader label="Prezzo iniziale" sortKey="addedPrice" view={view} onSort={handleSort} className="numeric col--secondary" />
                    <th className="col--secondary">Mercato</th>
                    <th className="col--secondary">Analisi AI</th>
                    <SortableHeader label="Ultima quotazione" sortKey="lastPriceAt" view={view} onSort={handleSort} className="col--tertiary" />
                    <SortableHeader label="Aggiunto il" sortKey="addedAt" view={view} onSort={handleSort} className="col--tertiary" />
                    <th aria-label="Azioni" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <Fragment key={item.id}>
                      {/* La riga seleziona il titolo del pannello grafico; per
                          tastiera e screen reader la stessa informazione resta
                          sul pulsante "Storico". */}
                      <tr
                        className={
                          isWide && selectedItem?.id === item.id
                            ? 'watchlist__row watchlist__row--selected'
                            : 'watchlist__row'
                        }
                        onClick={() => setSelectedItemId(item.id)}
                      >
                        <td>
                          <div className="watchlist__instrument" title={item.notes ?? undefined}>
                            {/* Scheda del titolo (SPEC-016): il click sul nome naviga,
                                quello sul resto della riga continua a selezionare il
                                pannello grafico. */}
                            <Link
                              to={`/instruments/${item.instrument.id}`}
                              className="watchlist__instrument-name"
                              onClick={(event) => event.stopPropagation()}
                            >
                              <InstrumentLogo instrumentId={item.instrument.id} name={item.instrument.name} />
                              {item.instrument.name}
                            </Link>
                            <span className="watchlist__instrument-meta">
                              {item.instrument.isin ?? item.instrument.symbol ?? '—'}
                              {item.inPortfolio && <> · in portafoglio</>}
                            </span>
                            <AutoSourceHint
                              item={item}
                              onKeep={() => keepItem.mutate(item.id)}
                              keeping={keepItem.isPending && keepItem.variables === item.id}
                            />
                            {!item.lastPrice && <MappingHint item={item} />}
                          </div>
                        </td>
                        <td className="numeric">
                          <PriceCell
                            amount={item.lastPrice}
                            currency={item.lastPriceCurrency}
                            amountBase={item.lastPriceBase}
                            baseCurrency={item.baseCurrency}
                          />
                        </td>
                        <td className="numeric">
                          <DeltaBadge deltaPct={item.deltaPct} />
                        </td>
                        <td className="numeric col--secondary">
                          <PriceCell
                            amount={item.addedPrice}
                            currency={item.addedPriceCurrency}
                            amountBase={item.addedPriceBase}
                            baseCurrency={item.baseCurrency}
                          />
                        </td>
                        <td className="col--secondary">
                          <MarketBadge item={item} />
                        </td>
                        <td className="col--secondary">
                          <LatestAnalysisCell item={item} />
                        </td>
                        <td className="cell--minor col--tertiary">
                          {item.lastPriceAt ? formatDateTime(item.lastPriceAt) : '—'}
                        </td>
                        <td className="cell--minor col--tertiary">{formatDate(item.addedAt)}</td>
                        <td>
                          {/* Il flex sta su un div interno: un td con display:flex
                              esce dal layout tabellare e disallinea la cella
                              rispetto alla propria riga. */}
                          {/* Icone e non etichette (docs/TODO.md): quattro
                              parole per riga si mangiavano la larghezza che
                              serve ai numeri. L'etichetta NON sparisce, si
                              sposta in `aria-label`/`title` — il pulsante
                              conserva lo stesso nome per i lettori di schermo
                              (ADR-0008).

                              I due pulsanti che aprono un pannello sono
                              interruttori con `aria-pressed`, e l'etichetta
                              resta fissa: cambiarla in "Nascondi storico"
                              costringerebbe a leggere il tooltip per capire in
                              che stato si è, mentre lo stato è già visibile
                              nell'icona evidenziata e nel pannello aperto. */}
                          <div className="watchlist__actions">
                            <IconButton
                              icon="chart"
                              label="Storico"
                              aria-pressed={expandedItemId === item.id}
                              onClick={() => setExpandedItemId(expandedItemId === item.id ? null : item.id)}
                            />
                            <IconButton
                              icon="ai"
                              label="Analisi AI"
                              aria-pressed={expandedAnalysisItemId === item.id}
                              onClick={() =>
                                setExpandedAnalysisItemId(expandedAnalysisItemId === item.id ? null : item.id)
                              }
                            />
                            <IconButton
                              icon="wishlist"
                              label="Wishlist"
                              onClick={() =>
                                setWishlistFor({
                                  id: item.instrument.id,
                                  name: item.instrument.name,
                                  symbol: item.instrument.symbol,
                                })
                              }
                            />
                            <IconButton
                              icon="delete"
                              label="Rimuovi"
                              variant="danger"
                              loading={removeItem.isPending && removeItem.variables === item.id}
                              onClick={() => removeItem.mutate(item.id)}
                            />
                          </div>
                        </td>
                      </tr>
                      {expandedItemId === item.id && (
                        <tr>
                          <td colSpan={9} className="watchlist__history-cell">
                            <ItemHistory item={item} />
                          </td>
                        </tr>
                      )}
                      {expandedAnalysisItemId === item.id && (
                        <tr>
                          <td colSpan={9} className="watchlist__history-cell">
                            <AiAnalysisSection item={item} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </TableWrap>
              )}
            </>
          )}
        </Card>

        {isWide && selectedItem && (
          <aside className="watchlist__chart-panel">
            <ChartPanel item={selectedItem} />
          </aside>
        )}
      </div>
    </div>
  );
}
